import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import {
  DeleteButton,
  EditButton,
  GreenButton,
  PrimaryButton,
  PurpleButton,
} from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import { useTranslation } from 'react-i18next';
import { RootState, store } from '@/state';
import {
  deleteSubProjectInstallation,
  saveSubProjectInstallationsAsync,
} from '@/state/slices/subProjectMethodSlice';
import { ThunkDispatch, AnyAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { PlusCircleIcon } from '@heroicons/react/solid';
import SubProjectInstallationModal from './SubProjectInstallationModal';
import { formatCurrency } from '@/utils/helpers';
import { Currency } from '@/enums/Currency';
import {
  SubProjectInstallation,
  SubProjectInstallationsPayload,
} from '@/services/SubProjectMethodsService';

interface SubProjectInstallation {
  id: string;
  subProjectMethodId: string;
  equipmentId: string;
  description: string;
  isLocated: boolean;
  trend: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  exchangeRate: number;
  name?: string;
  totalToCurrency?: number;
  equipmentCurrency?: Currency;
}

function SubProjectInstallations() {
  const { id } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isInstallationModal, setIsInstallationModal] = useState(false);
  const [initInstallationData, setInitInstallationData] =
    useState<SubProjectInstallation>();
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();

  const { data, status } = useSelector(
    (state: RootState) => state.subProjectMethod!
  );
  let projectCurrency = data?.currency ?? Currency.EUR;
  let subProjectId = data?.id;
  let subProjectInstallations = useMemo(
    () => data?.subProjectInstallations ?? [],
    [data, status]
  );

  const ActionButtonsCell = ({ row, value }: any) => (
    <div>
      <DeleteButton
        onClick={() => {
          setSelectedRow(value);
          setIsModalVisible(true);
        }}
      >
        {t('SubProjectInstallations.deleteButton')}
      </DeleteButton>
      <EditButton
        type="button"
        onClick={() => {
          setInitInstallationData((pr) => row.original);
          setIsInstallationModal(true);
        }}
      >
        {t('SubProjectInstallations.editButton')}
      </EditButton>
    </div>
  );

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteSubProjectInstallation.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('SubProjectInstallations.actionsColumn'),
        accessor: 'id',
        Cell: ({ row, cell: { value } }: any) => (
          <ActionButtonsCell value={value} row={row} />
        ),
        Footer: (infos) => {
          return <>Total</>;
        },
      },
      {
        Header: t('SubProjectInstallations.installationIdColumn'),
        accessor: 'name',
      },
      {
        Header: t('SubProjectInstallations.descriptionColumn'),
        accessor: 'description',
        Footer: (infos) => {
          return <></>;
        },
      },

      {
        Header: t('SubProjectInstallations.isLocatedColumn'),
        accessor: 'isLocated',
        Cell: ({ cell: { value } }: any) => (
          <span>
            {value
              ? t('SubProjectInstallations.yes')
              : t('SubProjectInstallations.no')}
          </span>
        ),
        Footer: (infos) => {
          return <></>;
        },
      },
      {
        Header: t('SubProjectInstallations.trendColumn'),
        accessor: 'trend',
        Footer: (infos) => {
          return <></>;
        },
      },
      {
        Header: t('SubProjectInstallations.quantityColumn'),
        accessor: 'quantity',
        Footer: (infos) => {
          var total = useMemo(
            () => infos.rows.reduce((sum, row) => row.values.quantity + sum, 0),
            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectInstallations.unitPriceColumn'),
        accessor: 'unitPrice',
        Cell: ({ row, cell: { value } }: any) => {
          var currency = row.original['equipmentCurrency'];
          return <span> {formatCurrency(value, currency)} </span>;
        },
        Footer: (infos) => {
          return <></>;
        },
      },
      {
        Header: t('SubProjectInstallations.totalPriceColumn'),
        accessor: 'totalPrice',
        Cell: ({ row, cell: { value } }: any) => {
          var currency = row.original['equipmentCurrency'];
          return <span> {formatCurrency(value, currency)} </span>;
        },
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce(
                (sum, row) =>
                  parseFloat(row.values.totalPrice) + parseFloat(sum),
                0
              ),
            [infos.rows]
          );
          return <>{total.toFixed(4)}</>;
        },
      },
      {
        Header: t('SubProjectInstallations.exchangeRateColumn'),
        accessor: 'exchangeRate',
        Footer: (infos) => {
          return <></>;
        },
      },
      {
        Header: t('SubProjectInstallations.TotalWithCurrencyColumn'),
        accessor: 'totalToCurrency',
        Cell: ({ cell: { value } }: any) => (
          <span> {formatCurrency(value, projectCurrency)} </span>
        ),
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce(
                (sum, row) =>
                  parseFloat(row.values.totalToCurrency) + parseFloat(sum),
                0
              ),
            [infos.rows]
          );
          return <>{formatCurrency(total.toFixed(4), projectCurrency)}</>;
        },
      },
      {
        Header: t('SubProjectInstallations.commentsColumn'),
        accessor: 'comment',
      },
    ],
    [t, projectCurrency]
  );

  const queryClient = useQueryClient();

  const handleDeleteSubProjectInstallation = useMutation(
    async (id: string) => {
      await dispatch(deleteSubProjectInstallation(id));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'subProjectInstallations',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((subProjectInstallations?.length ?? 0) / pageSize);
  }, [pageSize, subProjectInstallations]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  const handleSaveInstallations = async () => {
    var payload: SubProjectInstallationsPayload = {
      subProjectInstallations: subProjectInstallations.map((sm) => ({
        ...sm,
        id: sm.id?.includes('new') ? null : sm.id,
      })),
      subProjectMethodId: data?.id!,
    };
    const result = await dispatch(saveSubProjectInstallationsAsync(payload));
  };

  return (
    <div>
      <PrimaryButton
        type="button"
        onClick={() => {
          setInitInstallationData(undefined);
          setIsInstallationModal(true);
        }}
        className="mt-5"
      >
        {t('expenses.add')}
      </PrimaryButton>

      {subProjectId && (
        <PurpleButton
          type="button"
          onClick={handleSaveInstallations}
          className="mt-5 py-2.5"
        >
          {t('stepper.save')}
        </PurpleButton>
      )}
      {status == 'loading' ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={subProjectInstallations?.length}
            data={subProjectInstallations ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
            exportExcel={false}
            hideToolbar={true}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          {isInstallationModal && (
            <SubProjectInstallationModal
              isOpen={isInstallationModal}
              initialData={initInstallationData}
              setIsModalVisible={setIsInstallationModal}
              projectId={subProjectId!}
              closeModal={() => setIsInstallationModal(false)}
            />
          )}
        </div>
      )}

      <Pagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        gotoPage={onPageChange}
        nextPage={nextPage}
        previousPage={previousPage}
        pageSize={pageSize}
        setPageSize={onPageSizeChange}
        canPreviousPage={pageIndex > 0}
        canNextPage={pageIndex < pageCount - 1}
      />
    </div>
  );
}

export default SubProjectInstallations;
